import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  eyebrow: string;
  title: string;
  summary: string;
  image: string;
}

const SLIDES: Slide[] = [
  {
    id: "w1",
    eyebrow: "热播悬疑 · 年度口碑",
    title: "暗涌",
    summary:
      "一桩尘封十五年的悬案因匿名举报信被重新翻开，刑警队长在追查真相的路上，逐渐走进自己的过往。",
    image:
      "https://readdy.ai/api/search-image?query=Wide%20cinematic%20hero%20still%20of%20a%20rain%20drenched%20city%20at%20night%20with%20deep%20red%20and%20amber%20reflections%20on%20wet%20streets%2C%20lone%20silhouette%20in%20distance%2C%20moody%20atmospheric%20lighting%2C%20soft%20mist%2C%20premium%20film%20aesthetic%2C%20high%20detail&width=1920&height=1080&seq=hp-hero-01&orientation=landscape",
  },
  {
    id: "w7",
    eyebrow: "烧脑新片 · 影展最佳剧本",
    title: "午夜列车",
    summary:
      "一列从未停靠的午夜列车，载着七个各怀秘密的乘客。天亮之前，他们必须找出谁才是真正的旅人。",
    image:
      "https://readdy.ai/api/search-image?query=Wide%20cinematic%20hero%20still%20of%20a%20night%20train%20speeding%20through%20dark%20misty%20countryside%20with%20warm%20red%20window%20lights%2C%20rain%20streaked%20glass%2C%20moody%20atmospheric%20lighting%2C%20premium%20film%20aesthetic%2C%20high%20detail&width=1920&height=1080&seq=hp-hero-02&orientation=landscape",
  },
  {
    id: "w10",
    eyebrow: "东方奇幻 · 冒险巨制",
    title: "山海秘境",
    summary:
      "传说山海之间藏着一处能实现最深愿望的秘境，少年为救治师父踏上一场直面心魔的奇幻旅程。",
    image:
      "https://readdy.ai/api/search-image?query=Wide%20cinematic%20fantasy%20hero%20still%20of%20mythical%20floating%20mountains%20and%20misty%20valleys%20inspired%20by%20ancient%20Chinese%20mythology%2C%20warm%20amber%20and%20deep%20red%20sky%2C%20epic%20scale%2C%20high%20detail%20film%20aesthetic&width=1920&height=1080&seq=hp-hero-03&orientation=landscape",
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + SLIDES.length) % SLIDES.length);
  };

  const slide = SLIDES[index];

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden bg-foreground-950">
      {SLIDES.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} 焦点影视推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />

      <div className="relative z-10 w-full h-full px-4 md:px-6 flex flex-col justify-center">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-xs font-medium px-3 py-1.5 rounded-full mb-5">
            <i className="ri-fire-fill" />
            {slide.eyebrow}
          </span>
          <h1 className="font-heading font-black text-background-50 text-4xl md:text-6xl tracking-tight mb-4 md:mb-5">
            {slide.title}
          </h1>
          <p className="text-background-200 text-sm md:text-base leading-relaxed mb-7 max-w-xl">
            {slide.summary}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/detail/${slide.id}`}
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 font-medium px-6 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to="/category/works"
              className="inline-flex items-center gap-2 bg-background-50/10 border border-background-50/40 text-background-50 font-medium px-6 py-3 rounded-md hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-film-line text-lg" />
              浏览作品库
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-primary-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-primary-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-4 md:left-6 z-10 flex items-center gap-2">
        {SLIDES.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}